/*===================================
||
|| FormSelectDialogContext
||
===================================*/
import React, { useReducer, useMemo } from "react";
import PropTypes from "prop-types";

// context
import FormSelectDialogContext from "./context";
import { reducer } from "./context/store";

// components
import MountingWrapper from "./components/MountingWrapper";

// Provider
const FormSelectDialog = ({
    // required
    id,
    options,
    selectedOption,
    setSelectedOption,
    label,

    // optional
    children,
    hideOnScroll = false,

    controlLabelOverride = "",
    isDisabled = false,
    controlDisplay = "inline-flex",
    controlJustifyContent = "flex-start",
    dialogAlignment = "left", // left || center || right
    dialogOffset = 0,
    dialogPlacement = "bottom", // bottom || top
    dialogWidth = 300, // bottom || top
    dialogFontSize = 16,

    // for when consumer wants to control the isOpen state
    parentalControl,
}) => {
    // parental controls?
    const isControlled = parentalControl !== undefined;
    const isOpen = isControlled ? !!parentalControl?.isOpen : false;

    // These are props managed by consumer, not added to internal useReducer state
    const consumerProps = {
        id,
        options,
        selectedOption,
        setSelectedOption,
        label,
        controlLabelOverride,
        isDisabled,
        hideOnScroll,
        controlDisplay,
        controlJustifyContent,
        dialogAlignment,
        dialogOffset,
        dialogPlacement,
        dialogWidth,
        dialogFontSize,
        isControlled,
        parentalControl,
    };

    const initialArg = {
        isOpen,
        hasOpenedBefore: false, // we throw focus back onto openCloseButton after they close the dialog, not on mount
        openCloseButtonIsInFocus: false,
    };

    const [state, dispatch] = useReducer(reducer, initialArg);

    // useMemo so it does not pass value on every render
    const value = useMemo(
        () => ({ state, dispatch, ...consumerProps }),
        [state, dispatch, consumerProps],
    );

    return (
        <FormSelectDialogContext.Provider value={value}>
            <MountingWrapper>{children}</MountingWrapper>
        </FormSelectDialogContext.Provider>
    );
};

export default FormSelectDialog;

// Display Name
FormSelectDialogContext.displayName = "FormSelectDialog";

// prop-types
export const optionShape = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // used for comparing which is currently selected
    option: PropTypes.any.isRequired, // what shows in dropdown options display, and the Control Display if selected
    label: PropTypes.string.isRequired, // aria label
    controlLabelOverride: PropTypes.any, // This overrides the selected option.label in the Control Display
    // ... feel free to add any additional props needed; the object is preserved and passed back to consumer in `setSelectedOption`
};

// for when consumer wants to control the isOpen state
const parentalControlShape = {
    isOpen: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
};

FormSelectDialog.propTypes = {
    id: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape(optionShape)).isRequired,
    selectedOption: PropTypes.shape(optionShape).isRequired,
    setSelectedOption: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    children: PropTypes.any,
    controlLabelOverride: PropTypes.string, // This overrides the selected option.controlLabelOverride && option.label in the Control Display
    isDisabled: PropTypes.bool,
    hideOnScroll: PropTypes.bool,
    controlDisplay: PropTypes.string,
    controlJustifyContent: PropTypes.oneOf([
        "flex-start",
        "flex-end",
        "center",
        "space-between",
        "space-around",
        "space-evenly",
        "stretch",
    ]),
    dialogAlignment: PropTypes.oneOf(["left", "center", "right"]),
    dialogOffset: PropTypes.number,
    dialogPlacement: PropTypes.oneOf(["top", "bottom"]),
    dialogWidth: PropTypes.number,
    dialogFontSize: PropTypes.number,
    parentalControl: PropTypes.shape(parentalControlShape),
};
